import { Component, effect, input, output, QueryList, signal, ViewChild, ViewChildren } from '@angular/core';
import { GraphsBaseComponent } from '../../graphs-base.component';
import { DataTakerStaffDataModel, GraphAnnotationModel, GraphAnnotationType, GraphDataModel, GraphGrouping, GraphSeriesModel, PatientBehaviorModel, PatientTargetModel, TargetType } from '../../../core/api/models';
import { ChartComponent } from 'ng-apexcharts';
import { GraphApiService } from '../../../core/api/services/graph-api.service';
import { SupervisorApiService } from '../../../core/api/services/supervisor-api.service';
import { DatePipe } from '@angular/common';
import { AlertService } from '../../../shared/services/alert.service';
import { AuthService } from '../../../auth/auth.service';
import { StaffApiService } from '../../../core/api/services/staff-api.service';
import { PrintService, PrintHeaderInfo } from '../../../shared/services/print.service';
import { Utils } from '../../../core/utils';
import { Permissions } from '../../../core/api/models';
import { take } from 'rxjs/operators';
import { SeriesDataLabelOption, TrendLineDataLabelOption, AnnotationLabelBackgroundOption } from '../../graphs-base.component';

@Component({
  selector: 'app-mixed-graph',
  templateUrl: './mixed-graph.component.html',
  styleUrl: './mixed-graph.component.css',
  standalone: false,
})
export class MixedGraphComponent extends GraphsBaseComponent {
  @ViewChild('chart', { static: false }) chart: ChartComponent | undefined;
  @ViewChildren(ChartComponent) chartList!: QueryList<ChartComponent>;

  showAddAnnotation = signal<boolean>(false);
  annotationToEdit = signal< GraphAnnotationModel | null>( null);

  patientID = input.required<string>();
  patientIDAsNumber: number | null = null;

  startDate = signal<Date>(Utils.dateInPastBy(90));
  endDate = signal<Date>(new Date());
  forceGraphPeriod = signal<boolean>(true);

  isLoadingGraph: boolean = false;

  selectedTargets = input.required<PatientTargetModel[]>();
  selectedBehaviors = input.required<PatientBehaviorModel[]>();

  staffMembers = signal<DataTakerStaffDataModel[]>([]);
  selectedStaffMember: DataTakerStaffDataModel | null = null;

  graphData = signal<GraphDataModel | null>(null);

  seriesDataLabelOption = signal<SeriesDataLabelOption>(SeriesDataLabelOption.DotsAndValues);
  trendLineDataLabelOption = signal<TrendLineDataLabelOption>(TrendLineDataLabelOption.LineOnly);
  
  seriesDataLabelOptions = [
    { label: SeriesDataLabelOption.LineOnly, value: SeriesDataLabelOption.LineOnly },
    { label: SeriesDataLabelOption.Dots, value: SeriesDataLabelOption.Dots },
    { label: SeriesDataLabelOption.DotsAndValues, value: SeriesDataLabelOption.DotsAndValues },
    { label: SeriesDataLabelOption.FirstAndLastValues, value: SeriesDataLabelOption.FirstAndLastValues },
  ];
  
  trendLineDataLabelOptions = [
    { label: TrendLineDataLabelOption.LineOnly, value: TrendLineDataLabelOption.LineOnly },
    { label: TrendLineDataLabelOption.Dots, value: TrendLineDataLabelOption.Dots },
    { label: TrendLineDataLabelOption.DotsAndValues, value: TrendLineDataLabelOption.DotsAndValues },
    { label: TrendLineDataLabelOption.FirstAndLastValues, value: TrendLineDataLabelOption.FirstAndLastValues },
  ];

  displayTrendLine = signal<boolean>(false);
  segmentTrendLineByPhase = signal<boolean>(false);
  displayMergedSeries = signal<boolean>(false);

  private dateChangeTimer: any;

  constructor(
    private graphApiService: GraphApiService,
    private supervisorApiService: SupervisorApiService,
    private staffApiService: StaffApiService,
    private datePipe: DatePipe,
    private alertService: AlertService,
    private authService: AuthService,
    private printService: PrintService
  ) {
    super();
    this.loadDefaultGraphOptions();
    this.isGrouped.set(true);

    // Set default date option to 3 months (index 6)
    this.dateOption = this.dateOptions[6].value;
  }

  ngOnInit(): void {

    this.groupingOptions = this.groupingOptionsIncludingStaffOption;
    this.groupingOption = this.groupingOptions[2].value; // Daily

    this.patientIDAsNumber = Number(this.patientID());
  }

  loadDataForGraph() {

    // only load data if there are more than two items selected
    if (this.selectedTargets().length + this.selectedBehaviors().length <= 1) {
      this.alertService.error('Please select at least two targets or behaviors to start comparison mode.');
      this.isLoadingGraph = false;
      return;
    }

    var startTime = this.startDate();
    var endTime = this.endDate();

    const targetIDs = this.selectedTargets().map(target => target.ID!).filter(id => id !== null);
    const behaviorIDs = this.selectedBehaviors().map(behavior => behavior.ID!).filter(id => id !== null);

    // reset the merged series option if the current selection no longer allows it
    if (!this.shouldDisplayMergedSeriesOption()) {
      this.displayMergedSeries.set(false);
    }

    this.isLoadingGraph = true;

    this.graphApiService.GetMixedGraph({
      patientID: Number(this.patientID()),
      targetIDs: targetIDs,
      behaviorIDs: behaviorIDs,
      graphGrouping: this.groupingOption,
      staffID: this.selectedStaffMember?.StaffID ?? undefined,
      includeTrendLine: this.displayTrendLine(),
      segmentTrendLineByPhase: this.displayTrendLine() && this.segmentTrendLineByPhase(),
      mergeSeries: this.displayMergedSeries(),
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
    }).subscribe((data) => {
      console.log(data);

      this.staffMembers.set(data.DataTakers ?? []);

      // Add placeholder data points for the start and end of the selected period
      if (this.forceGraphPeriod()) {
        const startDate = this.startDate().toISOString();
        const endDate = this.endDate().toISOString();

        if (data.Series && data.Series.length > 0) {
          for (const series of data.Series) {
            if (series.DataPoints && series.DataPoints.length > 0) {
              const firstDataPoint = series.DataPoints[0];
              const lastDataPoint =
                series.DataPoints[series.DataPoints.length - 1];

              if (firstDataPoint.Date !== startDate) {
                series.DataPoints.unshift({
                  Date: startDate,
                  Value: null,
                });
              }

              if (lastDataPoint.Date !== endDate) {
                series.DataPoints.push({ Date: endDate, Value: null });
              }
            } else {
              // If no points in series, add placeholders for the entire period
              series.DataPoints = [
                { Date: startDate, Value: null },
                { Date: endDate, Value: null },
              ];
            }
          }
        }
      }

      this.graphData.set(data);
      this.annotations.set(data.Annotations ?? []);
      this.updateAnnotationDisplayGroupCheckmarks();
      this.updateGraph();
      this.isLoadingGraph = false;
    });
  }

  shouldDisplayAddAnnotationButton(): boolean {
    return this.hasSupervisorPermission();
  }

  hasSupervisorPermission(): boolean {
    const userInfo = this.authService.userInfo;
    if (!userInfo || !userInfo.Permissions) {
      return false;
    }

    return userInfo.Permissions.includes(Permissions.SparkApp_Supervisor);
  }

  shouldDisplayToggleGrouped(): boolean {
    return true;
  }

  // Magnitude group of the series plotted on the mixed graph for a target;
  // merging series is only allowed when every selected item is on the same magnitude.
  private magnitudeForTarget(target: PatientTargetModel): string | null {
    switch (target.TargetType) {
      case TargetType.DiscreteTrialTeaching:
      case TargetType.Echoic:
        return 'percentage';
      case TargetType.Duration:
        return 'duration-minutes';
      case TargetType.Frequency:
        return 'count';
      case TargetType.Quantity:
        return 'quantity-' + (target.QuantityUnit ?? '');
      case TargetType.FluencyRate:
        return 'fluency-rate';
      default:
        // target types without a series on the mixed graph
        return null;
    }
  }

  shouldDisplayMergedSeriesOption(): boolean {
    const magnitudes = [
      ...this.selectedTargets().map((target) => this.magnitudeForTarget(target)),
      // behaviors are always plotted as occurrence counts on the mixed graph
      ...this.selectedBehaviors().map(() => 'count'),
    ];

    if (magnitudes.length < 2 || magnitudes.some((magnitude) => magnitude === null)) {
      return false;
    }

    return magnitudes.every((magnitude) => magnitude === magnitudes[0]);
  }

  onToggleMergedSeries() {
    this.loadDataForGraph();
  }

  shouldDisplayGraph(): boolean {
    return (
      this.isLoadingGraph === false &&
      (this.selectedTargets().length + this.selectedBehaviors().length > 1) &&
      !this.isGraphEmpty()
    );
  }

  isGraphEmpty(): boolean {
    // check if the graph data is empty
    if (this.graphData() == null) {
      return true;
    }

    // check if series is empty
    if (this.graphData()?.Series?.length == 0) {
      return true;
    }

    return false;
  }

  formatDateForInput(date: Date): string {
    return Utils.formatDateForInput(date);
  }

  onStartDateChange(event: any) {
    if (!event) return;
    
    // Clear any existing timer
    if (this.dateChangeTimer) {
      clearTimeout(this.dateChangeTimer);
    }
    
    // Set new timer
    this.dateChangeTimer = setTimeout(() => {
      const date = Utils.parseDateFromInput(event);
      this.startDate.set(date);
      this.dateOption = this.dateOptions[0].value; // Custom option value
      this.reloadGraphForDateChange();
    }, 1500); // Wait 1.5 seconds after last change
  }

  onEndDateChange(event: any) {
    if (!event) return;
    
    // Clear any existing timer
    if (this.dateChangeTimer) {
      clearTimeout(this.dateChangeTimer);
    }
    
    // Set new timer
    this.dateChangeTimer = setTimeout(() => {
      const date = Utils.parseDateFromInput(event);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      this.endDate.set(endOfDay);
      this.dateOption = this.dateOptions[0].value; // Custom option value
      this.reloadGraphForDateChange();
    }, 1500); // Wait 1.5 seconds after last change
  }

  onDateChange(event: any) {
    const selectedDate = event.target.valueAsDate;
    // date is coming in UTC, we need to convert it to local time
    const localDate = new Date(selectedDate.getTime() + selectedDate.getTimezoneOffset() * 60000);
    const localDateEndOfDay = new Date(localDate.getTime() + 23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000 + 999);

    this.startDate.set(localDate);
    this.endDate.set(localDateEndOfDay);
    //this.loadDataForGraph(this.selectedTargets()[0]!.ID!);
  }

  onToggleGrouped() {
    this.updateGraph();
  }

  onDateOptionChange(event: any) {
    // Don't update if custom is selected
    if (event.value === this.dateOptions[0].value) {
      return;
    }
    
    // Update start date based on the selected option
    this.startDate.set(event.value);
    
    // Set end date to end of day today
    const endOfDayToday = new Date(new Date().getTime() + 23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000 + 999);
    this.endDate.set(endOfDayToday);

    this.reloadGraphForDateChange();
  }

  private reloadGraphForDateChange(): void {
    if (this.selectedTargets().length + this.selectedBehaviors().length > 1) {
      this.loadDataForGraph();
    }
  }

  onGroupingOptionChange(event: any) {
    this.groupingOption = event.value;

    if (this.groupingOption == GraphGrouping.ByStaffMember) {
      this.selectedStaffMember = null;
    }

    this.loadDataForGraph();
  }

  onForceGraphPeriodChange(checked: boolean) {
    this.forceGraphPeriod.set(checked);
    this.loadDataForGraph();
  }

  onStaffMemberChange(event: any) {
    this.selectedStaffMember = event.value;
    this.loadDataForGraph();
  }

  onSeriesDataLabelOptionChange() {
    this.updateGraph();
  }

  onTrendLineDataLabelOptionChange() {
    this.updateGraph();
  }

  override getDataLabelsConfig(): any {
    const seriesOption = this.seriesDataLabelOption();
    const trendLineOption = this.trendLineDataLabelOption();
    const seriesCount = this.graphData()?.Series?.length ?? 0;
    const hasTrendLine =
      this.displayTrendLine() && seriesCount > 0 && seriesCount <= 2;

    return this.buildSeriesTrendDataLabelsConfig(seriesOption, trendLineOption, {
      hasTrendLine,
      seriesCount: Math.max(seriesCount, 1),
      alternateTrendLines: false,
    });
  }

  getEnabledOnSeries(seriesOption: SeriesDataLabelOption, trendLineOption: TrendLineDataLabelOption): number[] {
    const enabled: number[] = [];
    
    // Series (index 0) - enabled if not LineOnly
    if (seriesOption !== SeriesDataLabelOption.LineOnly) {
      enabled.push(0);
    }
    
    // Trend line (index 1) - enabled if not LineOnly and trend line is displayed
    if (this.displayTrendLine() && trendLineOption !== TrendLineDataLabelOption.LineOnly) {
      enabled.push(1);
    }
    
    return enabled;
  }

  override getMarkersConfig(): any {
    const seriesOption = this.seriesDataLabelOption();
    const trendLineOption = this.trendLineDataLabelOption();
    const series = this.graphData()?.Series ?? [];
    const seriesCount = series.length;
    const hasTrendLine =
      this.displayTrendLine() && seriesCount > 0 && seriesCount <= 2;
    const graphData = this.graphData();
    const seriesDataList = graphData
      ? series.map((s) => this.processSeriesDataWithBreaks(s, graphData).data)
      : [];
    const colors = this.resolveChartColors(series);

    return this.buildSeriesTrendMarkersConfig(seriesOption, trendLineOption, {
      hasTrendLine,
      seriesCount: Math.max(seriesCount, 1),
      alternateTrendLines: false,
      seriesDataList,
      colors,
    });
  }

  // For multi-series charts where we know the exact number of series (e.g., grouped by staff per target),
  // adapt markers to apply selection consistently across all series in that chart
  private getMarkersConfigForSeriesCount(seriesCount: number): any {
    const seriesOption = this.seriesDataLabelOption();
    const trendLineOption = this.trendLineDataLabelOption();
    const hasTrendLine = this.displayTrendLine();
    const graphData = this.graphData();
    const series = graphData?.Series ?? [];
    const seriesDataList = graphData
      ? series.slice(0, seriesCount).map((s) => this.processSeriesDataWithBreaks(s, graphData).data)
      : [];
    const colors = this.resolveChartColors(series).slice(0, seriesCount);

    return this.buildSeriesTrendMarkersConfig(seriesOption, trendLineOption, {
      hasTrendLine,
      seriesCount,
      alternateTrendLines: hasTrendLine,
      seriesDataList,
      colors,
    });
  }

  // For multi-series charts where we know the exact number of series, adapt data labels configuration
  private getDataLabelsConfigForSeriesCount(seriesCount: number): any {
    const seriesOption = this.seriesDataLabelOption();
    const trendLineOption = this.trendLineDataLabelOption();
    const hasTrendLine = this.displayTrendLine();

    return this.buildSeriesTrendDataLabelsConfig(seriesOption, trendLineOption, {
      hasTrendLine,
      seriesCount,
      alternateTrendLines: hasTrendLine,
    });
  }

  shouldDisplayAnnotationLabelBackgroundOption(): boolean {
    return true;
  }

  onAnnotationLabelBackgroundOptionSelected(value: AnnotationLabelBackgroundOption): void {
    this.annotationLabelBackgroundOption.set(value);
    this.onAnnotationLabelBackgroundOptionChange();
  }

  onDeclutterAnnotationsSelected(value: boolean): void {
    this.declutterAnnotations.set(value);
    this.onDeclutterAnnotationsChange();
  }

  onAnnotationLabelPositionChange(
    annotation: GraphAnnotationModel,
    labelPosition: 'start' | 'middle' | 'end'
  ): void {
    this.applyAnnotationLabelPositionChange(
      annotation,
      labelPosition,
      this.graphData(),
      (value) => this.graphData.set(value),
      (updated) => {
        this.graphApiService.SaveGraphAnnotation({ body: updated }).subscribe({
          next: () => this.refreshChartAnnotations(),
          error: (error) =>
            this.alertService.error(
              'Error saving annotation - ' + Utils.getErrorMessageFromErrorResponse(error)
            ),
        });
      }
    );
  }

  protected override resolveDeclutterAnnotationCandidates(
    graphData: GraphDataModel | null | undefined
  ): GraphAnnotationModel[] {
    if (!graphData?.Annotations?.length) {
      return [];
    }

    if (this.isGrouped() && (graphData.Series?.length ?? 0) > 0) {
      const seen = new Set<string>();
      const candidates: GraphAnnotationModel[] = [];
      for (const series of graphData.Series ?? []) {
        for (const annotation of this.getAnnotationsShownOnGraph(graphData, series)) {
          const key = this.annotationDisplayKey(annotation);
          if (seen.has(key)) {
            continue;
          }
          seen.add(key);
          candidates.push(annotation);
        }
      }
      return candidates;
    }

    return this.getAnnotationsShownOnGraph(graphData, graphData.Series?.[0] ?? null);
  }

  getAnnotationListItems() {
    return this.getVisibleAnnotationListItems(this.startDate(), this.endDate());
  }
  
  onToggleTrendLine() {
    this.loadDataForGraph();
  }

  onToggleSegmentTrendLineByPhase() {
    this.loadDataForGraph();
  }

  override updateGraph() {
    console.log('updateGraph start - ' + new Date().toISOString());
    if (!this.graphData()) {
      // hide graph
      this.chartOptions.series = [];
      return;
    }

    this.loadDefaultLineGraphOptionsForMixed(this.graphData()!, this.seriesDataLabelOption(), true, this.shouldRenderAnnotations(), this.displayTrendLine(), false);

    // Set a default filename immediately to prevent random filename generation
    this.setDefaultExportFilename();

    this.applyChartOptions(this.chart, this.chartOptions);
    if (!this.isGrouped() && this.chartList?.length) {
      this.chartList.forEach((syncedChart, index) => {
        const syncedOptions = this.chartsOptions[index];
        if (syncedOptions) {
          this.applyChartOptions(syncedChart, syncedOptions);
        }
      });
    }

    console.log('updateGraph end - ' + new Date().toISOString());
  }

  // graph functions
  loadDefaultLineGraphOptionsForMixed(
    graphData: GraphDataModel, 
    displayDataPointOption: SeriesDataLabelOption = SeriesDataLabelOption.DotsAndValues, 
    displayLegend: boolean = true, 
    displayAnnotations: boolean = true, 
    displayTrendLineForProgramGraph: boolean = false,
    limitYAxisValues: boolean = false,
    displayTotalSessionTimeAndTargetDataTimeInTooltip: boolean = false,
    displayPromptDataInTooltip: boolean = false,
    displayMasteredTargetsTooltip: boolean = false
  ) {
    if (this.isGrouped()) {
      // Grouped Graph or Normal Graphs
      console.log('Mixed Graph - Grouped Graph');
      this.isGrouped.set(true);
      this.chartOptions = {
        series: graphData.Series!.map((series, index) =>
          this.mapLineSeriesForChart(series, graphData, index)
        ),
        colors: this.resolveChartColors(graphData.Series),
        legend: this.buildLegendConfig(displayLegend),
        chart: {
          height: 350,
          type: 'line',
          ...this.sharedChartConfig,
        },
        dataLabels: this.getDataLabelsConfig(),
        stroke: {
          curve: 'straight',
        },
        grid: {
          padding: {
            right: 30,
            left: 20,
          },
        },
        markers: this.getMarkersConfig(),
        tooltip: {
          custom: this.createCustomTooltipSimple(graphData, graphData.DataTakers ?? [])
        },
        title: {
          text: graphData.Title ?? 'Chart',
        },
        xaxis: {
          type: 'datetime',
          title: {
            text: graphData.XAxisTitle ?? 'X Axis',
          },
          labels: {
            datetimeUTC: false,
          },
        },
        yaxis: {
          min: limitYAxisValues ? 0 : undefined,
          max: limitYAxisValues ? 100 : undefined,
          title: {
            text: graphData.YAxisTitle ?? 'Y Axis',
          },
          labels: {
            formatter: (value: number) => {
              // If it's a whole number, don't show decimal places
              if (Number.isInteger(value)) {
                return value.toString();
              }
              // If it's a decimal, round to 2 decimal places
              return value.toFixed(2);
            }
          }
        },
        annotations: this.buildAnnotationsConfig(graphData, null, displayAnnotations),
      };
    } else {
      // Synced Graphs
      this.isGrouped.set(false);

      // if grouping option is by staff member, we need to group the graphs by staff member
      if (this.groupingOption === GraphGrouping.ByStaffMember) {
        console.log('Program Graph - Synced Graphs - By Staff Member (group by PatientTargetID)');
        // Group series by PatientTargetID so each chart shows all staff series for that target
        const seriesByTargetId = new Map<number, GraphSeriesModel[]>();
        (graphData.Series || []).forEach((s) => {
          const targetId = s.PatientTargetID as number | undefined;
          if (targetId == null) return;
          const arr = seriesByTargetId.get(targetId) || [];
          arr.push(s);
          seriesByTargetId.set(targetId, arr);
        });
        this.chartsOptions = Array.from(seriesByTargetId.entries()).map(([targetId, seriesList]) => {
          const targetName = seriesList[0].PatientTargetName ?? 'No Name';
          return {
            series: seriesList.map((s) => ({
              name: s.Name ?? 'No Name',
              data: this.processSeriesDataWithBreaks(s, graphData).data,
            })),
            legend: displayLegend ? {
              position: 'top',
              onItemClick: { toggleDataSeries: true },
              onItemHover: { highlightDataSeries: true },
            } : { show: false },
            chart: {
              id: `target-${targetId}`,
              group: 'group1',
              height: 350,
              type: 'line',
              ...this.sharedChartConfig,
            },
            dataLabels: this.getDataLabelsConfigForSeriesCount(seriesList.length),
            stroke: { curve: 'straight' },
            grid: { padding: { right: 30, left: 20 } },
            markers: this.getMarkersConfigForSeriesCount(seriesList.length),
            tooltip: {
              custom: this.createCustomTooltipSimple(graphData, graphData.DataTakers ?? [])
            },
            title: {
              text: `Target ${targetName}`,
            },
            xaxis: {
              type: 'datetime',
              title: { text: graphData.XAxisTitle ?? 'X Axis' },
              labels: { datetimeUTC: false },
            },
            yaxis: {
              min: limitYAxisValues ? 0 : undefined,
              max: limitYAxisValues ? 100 : undefined,
              title: { text: graphData.YAxisTitle ?? 'Y Axis' },
            },
            annotations: displayAnnotations && seriesList.length > 0 ? {
              xaxis: this.xaxisAnnotationsFromGraphDataForSyncedPrograms(graphData, seriesList[0]),
              yaxis: this.yaxisAnnotationsFromGraphDataForSyncedPrograms(graphData, seriesList[0]),
              points: this.pointsAnnotationsFromGraphDataForSyncedPrograms(graphData, seriesList[0]),
              texts: this.textsAnnotationsFromGraphDataForSyncedPrograms(graphData, seriesList[0]),
            } : undefined,
          };
        });
        console.log('Program Graph - Synced Graphs - Grouped by TargetID - Charts Options', this.chartsOptions);
      }
      else {
        // if it is a program graph and trend line is selected, we need to graph types two by two;
        // every other graph in the series will be the trend line in this case;
        if (displayTrendLineForProgramGraph) { // Program Graph with Trend Line
          console.log('Program Graph with Trend Line');
          var allSeries = graphData.Series!;
          var notTrendLineSeries = allSeries.filter((series, index) => index % 2 === 0);
          var trendLineSeries = allSeries.filter((series, index) => index % 2 === 1);

        this.chartsOptions = notTrendLineSeries.map((series) => {
          var trendLineSeriesForGraph = trendLineSeries.shift();
          return {
            series: [
              {
                name: series.Name ?? 'No Name',
                data: this.processSeriesDataWithBreaks(series, graphData).data,
              },
              // pop the first series from the trendLineSeries array and add it to the series array
              {
                name: trendLineSeriesForGraph!.Name ?? 'No Name',
                data: this.processSeriesDataWithBreaks(trendLineSeriesForGraph!, graphData).data,
              },
            ],
            legend: displayLegend
              ? {
                  position: 'top',
                  onItemClick: {
                    toggleDataSeries: true,
                  },
                  onItemHover: {
                    highlightDataSeries: true,
                  },
                }
              : {
                  show: false,
                },
            chart: {
              id: series.Name ?? 'No Name',
              group: 'group1',
              height: 350,
              type: 'line',
              ...this.sharedChartConfig,
            },
            dataLabels: this.getDataLabelsConfigForSeriesCount(2),
            stroke: {
              curve: 'straight',
            },
            grid: {
              padding: {
                right: 30,
                left: 20,
              },
            },
            markers: this.getMarkersConfigForSeriesCount(2),
            tooltip: {
              custom: this.createCustomTooltipSimple(graphData, graphData.DataTakers ?? [])
            },
            title: {
              text: series.Name ?? 'Chart',
            },
            xaxis: {
              type: 'datetime',
              title: {
                text: graphData.XAxisTitle ?? 'X Axis',
              },
              labels: {
                datetimeUTC: false,
              },
            },
            yaxis: {
              min: limitYAxisValues ? 0 : undefined,
              max: limitYAxisValues ? 100 : undefined,
              title: {
                text: graphData.YAxisTitle ?? 'Y Axis',
                },
              labels: {
                formatter: (value: number) => {
                  // If it's a whole number, don't show decimal places
                  if (Number.isInteger(value)) {
                    return value.toString();
                  }
                  // If it's a decimal, round to 2 decimal places
                  return value.toFixed(2);
                }
              }
              },
              annotations: displayAnnotations ? {
                xaxis: this.xaxisAnnotationsFromGraphDataForSyncedPrograms(graphData, series),
                yaxis: this.yaxisAnnotationsFromGraphDataForSyncedPrograms(graphData, series),
                points: this.pointsAnnotationsFromGraphDataForSyncedPrograms(graphData, series),
                texts: this.textsAnnotationsFromGraphDataForSyncedPrograms(graphData, series),
              } : undefined,
          }
        });
      } else {
        // Synced Graphs
        console.log('Program Graph - Synced Graphs');
        this.chartsOptions = graphData.Series!.map((series) => {
          return {
            series: [
              {
                name: series.Name ?? 'No Name',
                data: this.processSeriesDataWithBreaks(series, graphData).data,
              },
            ],
            legend: displayLegend
              ? {
                  position: 'top',
                  onItemClick: {
                    toggleDataSeries: true,
                  },
                  onItemHover: {
                    highlightDataSeries: true,
                  },
                }
              : {
                  show: false,
                },
            chart: {
              id: series.Name ?? 'No Name',
              group: 'group1',
              height: 350,
              type: 'line',
              ...this.sharedChartConfig,
            },
            dataLabels: this.getDataLabelsConfig(),
            stroke: {
              curve: 'straight',
            },
            grid: {
              padding: {
                right: 30,
                left: 20,
              },
            },
            markers: this.getMarkersConfig(),
            tooltip: {
              custom: this.createCustomTooltipSimple(graphData, graphData.DataTakers ?? [])
            },
            title: {
              text: series.Name ?? 'Chart',
            },
            xaxis: {
              type: 'datetime',
              title: {
                text: graphData.XAxisTitle ?? 'X Axis',
              },
              labels: {
                datetimeUTC: false,
              },
            },
            yaxis: {
              min: limitYAxisValues ? 0 : undefined,
              max: limitYAxisValues ? 100 : undefined,
              title: {
                text: graphData.YAxisTitle ?? 'Y Axis',
                },
              labels: {
                formatter: (value: number) => {
                  // If it's a whole number, don't show decimal places
                  if (Number.isInteger(value)) {
                    return value.toString();
                  }
                  // If it's a decimal, round to 2 decimal places
                  return value.toFixed(2);
                }
              }
              },
            annotations: displayAnnotations ? {
              xaxis: this.xaxisAnnotationsFromGraphDataForSyncedPrograms(graphData, series),
              yaxis: this.yaxisAnnotationsFromGraphDataForSyncedPrograms(graphData, series),
              points: this.pointsAnnotationsFromGraphDataForSyncedPrograms(graphData, series),
              texts: this.textsAnnotationsFromGraphDataForSyncedPrograms(graphData, series),
            } : undefined,
          }
        });
      }
      }
    }
  }

  private setDefaultExportFilename(): void {
    // Set a basic filename immediately to prevent ApexCharts from generating a random one
    const graphType = 'Mixed Graph';
    const targetNames = this.selectedTargets().map(target => target.TargetName).join(', ');
    const behaviorNames = this.selectedBehaviors().map(behavior => behavior.PatientBehaviorName).join(', ');
    const defaultFilename = `${graphType}-${targetNames}-${behaviorNames}`.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '_').substring(0, 100);
    
    if (this.chartOptions.chart?.toolbar?.export) {
      if (!this.chartOptions.chart.toolbar.export.csv) {
        this.chartOptions.chart.toolbar.export.csv = {};
      }
      if (!this.chartOptions.chart.toolbar.export.svg) {
        this.chartOptions.chart.toolbar.export.svg = {};
      }
      if (!this.chartOptions.chart.toolbar.export.png) {
        this.chartOptions.chart.toolbar.export.png = {};
      }
      this.chartOptions.chart.toolbar.export.csv.filename = defaultFilename;
      this.chartOptions.chart.toolbar.export.svg.filename = defaultFilename;
      this.chartOptions.chart.toolbar.export.png.filename = defaultFilename;
    }
  }

  // annotation graph functions
  // Annotations
  xaxisAnnotationsFromGraphDataForSyncedPrograms(
    graphData: GraphDataModel,
    series: GraphSeriesModel
  ) {
    return this.xaxisAnnotationsFromGraphData(graphData, series);
  }

  yaxisAnnotationsFromGraphDataForSyncedPrograms(
    graphData: GraphDataModel,
    series: GraphSeriesModel
  ) {
    return this.yaxisAnnotationsFromGraphData(graphData, series);
  }

  pointsAnnotationsFromGraphDataForSyncedPrograms(
    graphData: GraphDataModel,
    series: GraphSeriesModel
  ) {
    return this.pointsAnnotationsFromGraphData(graphData, series);
  }

  textsAnnotationsFromGraphDataForSyncedPrograms(
    graphData: GraphDataModel,
    series: GraphSeriesModel
  ) {
    return this.textsAnnotationsFromGraphData(graphData, series);
  }

  /**
   * Formats annotation label text to use multiple lines if it exceeds 30 characters
   */
  protected override formatAnnotationLabelText(
    text: string
  ): string | string[] {
    if (text.length <= 30) {
      return text;
    }

    // Split the text into words and filter out empty strings
    const words = text.split(' ').filter((word) => word.length > 0);
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      // If adding this word would exceed 30 characters, start a new line
      if (currentLine.length + word.length + 1 > 30 && currentLine.length > 0) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        if (currentLine.length === 0) {
          currentLine = word;
        } else {
          currentLine += ' ' + word;
        }
      }
    }

    // Add the last line if it has content
    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    // Return array of strings for multiline support in ApexCharts
    // Pad each line to exactly 30 characters with whitespace
    if (lines.length > 1) {
      return lines.map((line) => line.padEnd(30, ' '));
    }

    return text;
  }

  /**
   * Handles print click from the graph toolbar
   */
  override handleDownloadPngClick(): void {
    if (!this.graphData()) return;
    this.buildReportHeaderInfo().then((header) => {
      const isGrouped = this.isGrouped();
      if (isGrouped) {
        if (!this.chart) return;
        this.printService.downloadPngPage(this.chart as any, header);
      } else {
        const charts = this.chartList ? this.chartList.toArray() : [];
        if (charts.length === 0) return;
        this.printService.downloadPngPageForCharts(charts as any, header);
      }
    });
  }

  override handleDownloadPdfClick(): void {
    if (!this.graphData()) return;
    this.buildHeaderInfoAndDownloadPdf();
  }

  private async buildReportHeaderInfo(): Promise<PrintHeaderInfo> {
    let patientName = 'Unknown Patient';
    try {
      const patient = await this.staffApiService.GetPatientInfo({ id: Number(this.patientID()) }).pipe(take(1)).toPromise();
      if (patient) {
        patientName = `${patient.FirstName || ''} ${patient.LastName || ''}`.trim() || patient.PatientName || 'Unknown Patient';
      }
    } catch {}
    const startDate = this.startDate();
    const endDate = this.endDate();
    const timePeriod = `${this.datePipe.transform(startDate, 'MMM dd, yyyy')} - ${this.datePipe.transform(endDate, 'MMM dd, yyyy')}`;
    const graphType = this.getMixedGraphTypeDisplayName();
    const subtitleItems: string[] = [];
    if (this.selectedTargets().length > 0) subtitleItems.push(...this.selectedTargets().map(t => `${t.TargetName || 'Unnamed Target'} (${Utils.patientTargetTypeLabel(t.TargetType!)})`));
    if (this.selectedBehaviors().length > 0) subtitleItems.push(...this.selectedBehaviors().map(b => `${b.PatientBehaviorName || 'Unnamed Behavior'}`));
    const selectedTargets = this.selectedTargets().map(t => t.TargetName || 'Unknown Target');
    const selectedBehaviors = this.selectedBehaviors().map(b => b.PatientBehaviorName || 'Unknown Behavior');
    const visibleAnnotations = this.annotations()
      .filter(annotation => {
        if (!this.isAnnotationDisplayed(annotation)) return false;
        if (annotation.StartDate) {
          const annotationDate = new Date(annotation.StartDate);
          return annotationDate >= startDate && annotationDate <= endDate;
        }
        return true;
      })
      .map(annotation => {
        if (annotation.GraphAnnotationType === GraphAnnotationType.XAxis) {
          return annotation.Label + ' - ' + Utils.graphAnnotationTypeLabel(annotation.GraphAnnotationType!) + ' - ' + new Date(annotation.StartDate!).toLocaleDateString();
        } else {
          return annotation.Label + ' - ' + Utils.graphAnnotationTypeLabel(annotation.GraphAnnotationType!) + ' - ' + annotation.YValueStart;
        }
      });
    const headerInfo: PrintHeaderInfo = {
      graphTitle: this.graphData()?.Title || 'Mixed Graph',
      graphType,
      patientName,
      timePeriod,
      groupingLabel: this.groupingOptionLabel(),
      displayOptions: this.getDisplayOptions(),
      subtitleItems,
      selectedTargets,
      annotations: visibleAnnotations,
      staffMember: this.getStaffMember() ?? undefined
    };
    return headerInfo;
  }

  private groupingOptionLabel(): string {
    const groupingMap: { [key in GraphGrouping]: string } = {
      [GraphGrouping.BySession]: 'By Session',
      [GraphGrouping.Daily]: 'Daily',
      [GraphGrouping.Weekly]: 'Weekly',
      [GraphGrouping.Monthly]: 'Monthly',
      [GraphGrouping.Yearly]: 'Yearly',
      [GraphGrouping.ByStaffMember]: 'By Staff Member',
      [GraphGrouping.ByTrial]: 'By Trial',
    };
    return groupingMap[this.groupingOption];
  }

  private getDisplayOptions(): string[] {
    const options: string[] = [];
    if (this.shouldDisplayToggleGrouped()) {
      if (this.isGrouped()) options.push('Grouped'); else options.push('Synced');
    }
    if (this.displayMergedSeries()) {
      options.push('Merged');
    }
    options.push(`Series: ${this.seriesDataLabelOption()}`);
    if (this.displayTrendLine()) {
      options.push(`Trend Line: ${this.trendLineDataLabelOption()}`);
    }
    if (this.shouldDisplayAnnotationLabelBackgroundOption()) {
      options.push(`Annotation Labels: ${this.annotationLabelBackgroundOption()}`);
    }
    if (this.declutterAnnotations()) {
      options.push('Declutter Annotations');
    }
    return options;
  }

  private getStaffMember(): string | undefined {
    return this.selectedStaffMember?.FullName ?? undefined;
  }

  private async buildHeaderInfoAndDownloadPdf(): Promise<void> {
    const headerInfo: PrintHeaderInfo = await this.buildReportHeaderInfo();
    const isGrouped = this.isGrouped();
    if (isGrouped) {
      if (!this.chart) return;
      this.printService.downloadPdf(this.chart as any, headerInfo);
    } else {
      const charts = this.chartList ? this.chartList.toArray() : [];
      if (charts.length === 0) return;
      this.printService.downloadPdfForCharts(charts as any, headerInfo);
    }
  }

  private getMixedGraphTypeDisplayName(): string {
      return 'Mixed Graph';
  }

  // Annotation functions

  addAnnotationButtonClicked() {
    console.log('addAnnotationButtonClicked');
    const emptyAnnotation: GraphAnnotationModel = {
      PatientTargetIDs: this.selectedTargets().map(t => t.ID!),
      PatientBehaviorIDs: this.selectedBehaviors().map(b => b.ID!),
    };
    this.annotationToEdit.set(emptyAnnotation);
    this.showAddAnnotation.set(true);
  }

  editAnnotation(annotation: GraphAnnotationModel) {
    console.log('editAnnotation', annotation);
    this.annotationToEdit.set(annotation);
    this.showAddAnnotation.set(true);
  }

  onCloseAddAnnotation(withChanges: boolean = false) {
    console.log('onCloseAddAnnotation', withChanges);
    if (withChanges) {
      this.loadDataForGraph();
    }
    this.showAddAnnotation.set(false);
  }

}
